/* Traer archivos desde Google Drive.
 *
 * Google exige registrar cada dirección desde la que se inicia sesión, y no
 * acepta comodines: no se puede autorizar "*.tabularis.cl", y cada estudio
 * está en su subdominio. Por eso todo lo de Google ocurre en UNA página fija,
 * tabularis.cl/drive (pages/Drive/DriveApp.tsx), que se abre como ventana:
 * ahí se inicia sesión, se eligen los archivos con el selector de Google y se
 * descargan. Después la ventana se los entrega a la pestaña del estudio con
 * postMessage y se cierra.
 *
 * Así el panel no tiene que abrirle sus cabeceras de seguridad a Google (la
 * CSP de /drive es aparte, ver scripts/cloudfront-drive.sh), y el permiso que
 * se pide es el más chico que existe: solo los archivos que el abogado elige
 * (drive.file). Tabularis no ve el resto de su Drive.
 */

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
export const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';
/** Número del proyecto de Google Cloud: el selector lo pide para dar acceso a
 *  lo elegido con el permiso drive.file. */
export const GOOGLE_APP_ID = process.env.REACT_APP_GOOGLE_APP_ID || '';

/** Mientras no estén las credenciales de Google, el botón no aparece. */
export const driveDisponible = () => !!(GOOGLE_CLIENT_ID && GOOGLE_API_KEY && GOOGLE_APP_ID);

/** Dirección de la página de Drive: el dominio raíz, sin el subdominio del
 *  estudio. demo.tabularis.cl → https://tabularis.cl; demo.localhost:3000 →
 *  http://localhost:3000. */
export function origenDrive(): string {
  const { protocol, hostname, port } = window.location;
  const partes = hostname.split('.');
  const raiz = hostname.endsWith('localhost') ? 'localhost' : partes.slice(-2).join('.');
  return `${protocol}//${raiz}${port ? ':' + port : ''}`;
}

/** Qué pestañas pueden recibir archivos de la página de Drive: solo los
 *  paneles de los estudios. Lo comprueba la página antes de entregar nada. */
export function origenPermitido(origen: string): boolean {
  return /^https:\/\/[a-z0-9-]+\.tabularis\.cl$/.test(origen) || /^http:\/\/[a-z0-9-]+\.localhost(:\d+)?$/.test(origen);
}

export type MensajeDrive =
  | { tipo: 'tabularis-drive'; estado: 'archivos'; archivos: { ruta: string; tipo: string; blob: Blob }[] }
  | { tipo: 'tabularis-drive'; estado: 'cancelado' }
  | { tipo: 'tabularis-drive'; estado: 'error'; mensaje: string };

/** Abre la ventana de Drive y espera los archivos. Devuelve [] si se cerró
 *  sin elegir nada. Solo archivos: una carpeta completa no se puede traer con
 *  este permiso (ver pages/Drive/DriveApp.tsx). */
export function elegirDeDrive(): Promise<{ archivo: File; ruta: string }[]> {
  const origen = origenDrive();
  const url = `${origen}/drive?origen=${encodeURIComponent(window.location.origin)}`;
  const ancho = 960, alto = 720;
  const izq = Math.max(0, (window.screenX || 0) + (window.outerWidth - ancho) / 2);
  const arriba = Math.max(0, (window.screenY || 0) + (window.outerHeight - alto) / 2);
  const ventana = window.open(url, 'tabularis-drive', `popup,width=${ancho},height=${alto},left=${izq},top=${arriba}`);
  if (!ventana) return Promise.reject(new Error('El navegador bloqueó la ventana de Google Drive. Permite las ventanas emergentes de Tabularis y vuelve a intentarlo.'));

  return new Promise((resolver, rechazar) => {
    let terminado = false;
    const fin = () => { terminado = true; window.removeEventListener('message', alMensaje); clearInterval(vigia); };
    const alMensaje = (e: MessageEvent) => {
      if (e.origin !== origen || e.source !== ventana) return;
      const m = e.data as MensajeDrive;
      if (!m || m.tipo !== 'tabularis-drive') return;
      fin();
      if (m.estado === 'archivos') {
        resolver(m.archivos.map(a => ({
          ruta: a.ruta,
          archivo: new File([a.blob], a.ruta.split('/').pop() || 'archivo', { type: a.tipo || a.blob.type }),
        })));
      } else if (m.estado === 'error') rechazar(new Error(m.mensaje));
      else resolver([]);
    };
    window.addEventListener('message', alMensaje);
    // Si el abogado cierra la ventana sin elegir, no llega ningún mensaje.
    const vigia = setInterval(() => {
      if (ventana.closed && !terminado) {
        // Un mensaje enviado justo antes de cerrar todavía puede estar en cola.
        setTimeout(() => { if (!terminado) { fin(); resolver([]); } }, 300);
      }
    }, 500);
  });
}
